/* eslint-disable @typescript-eslint/no-base-to-string */
/* eslint-disable @typescript-eslint/no-dynamic-delete */
import parse from 'html-react-parser';
import { PageUrl } from '../../pages/utils/page-url';
import { isServer } from '../../utils/utils';
import { GTMPageType } from './model/GTMPageType';
import type { Basket, ProductItem } from '../../bff-api/baskets/model';
import type { Customer } from '../../bff-api/customers/model';
import type { Product, Products } from '../../bff-api/products/model';
import type { GTMEvent } from './model/GTMEvent';
import type { GTMEventName } from './model/GTMEventName';
import type { GTMPageData } from './model/GTMPageData';

const sensitiveProductFields: Array<keyof Product> = ['c_landedCost'];
const sensitiveProductItemFields: Array<keyof Partial<ProductItem>> = [
    'c_landedCost',
    'c_ExtendPlanId',
];
const sensitiveUserFields: Array<keyof Customer> = [
    'login',
    'gender',
    'creationDate',
    'c_confirmedEmailKey',
    'c_zipCode',
];
const sensitiveAddressFields = [
    'addressId',
    'address1',
    'creationDate',
    'lastModified',
    'preferred',
];

const removeBasketSensitiveFields = (basket: Basket): Basket => {
    const newBasket = {
        ...basket,
    };

    newBasket.productItems = newBasket.productItems?.map((item) => ({
        ...item,
        productName: parse(item.productName ?? '').toString(),
        itemText: parse(item.itemText ?? '').toString(),
    }));

    sensitiveProductItemFields.forEach((field) =>
        newBasket.productItems?.forEach((item) => delete item[field]),
    );

    return newBasket;
};

const removeProductSensitiveFields = (product: Product): Product => {
    const newProduct = {
        ...product,
        name: parse(product.name ?? '').toString(),
        pageTitle: parse(product.pageTitle ?? '').toString(),
        pageDescription: parse(product.pageDescription ?? '').toString(),
    } as Product;

    sensitiveProductFields.forEach((field) => delete newProduct[field]);

    return newProduct;
};

const removeProductsSensitiveFields = (products: Products): Products =>
    products.map(removeProductSensitiveFields);

const removeProductItemSensitiveFields = (productItem: ProductItem): ProductItem => {
    const newProductItem = { ...productItem };
    sensitiveProductItemFields.forEach((field) => delete newProductItem[field]);
    return newProductItem;
};

const removeCustomerSensitiveFields = (customer: Customer): Customer => {
    const user = { ...customer };
    sensitiveUserFields.forEach((field) => user && delete user[field]);

    if (user.addresses) {
        user.addresses = user.addresses.map((a) => ({ ...a }));
        user.addresses?.forEach((address) =>
            sensitiveAddressFields.forEach((field) => delete address[field]),
        );
    }

    return user;
};

export const removePageDataSensitiveFields = (data: GTMPageData): GTMPageData => {
    const newData = {
        ...data,
    };

    if (newData.user) {
        newData.user = removeCustomerSensitiveFields(newData.user);
    }

    if (newData.bag) {
        newData.bag = removeBasketSensitiveFields(newData.bag);
    }

    if (newData.product) {
        newData.product = removeProductSensitiveFields(newData.product);
    }

    if (newData.products) {
        newData.products = removeProductsSensitiveFields(newData.products);
    }

    return newData;
};

export const removeEventSensitiveFields = (event: GTMEvent): GTMEvent => {
    const filteredEvent = {
        ...event,
    };

    if (event.user) {
        filteredEvent.user = removeCustomerSensitiveFields(event.user);
    }

    if (event.bag) {
        filteredEvent.bag = removeBasketSensitiveFields(event.bag);
    }

    if (event.product) {
        filteredEvent.product = removeProductSensitiveFields(event.product);
    }

    if (event.products) {
        filteredEvent.products = removeProductsSensitiveFields(event.products);
    }

    if (event.productItem) {
        filteredEvent.productItem = removeProductItemSensitiveFields(event.productItem);
    }

    return filteredEvent;
};

export const getPageType = (): GTMPageType | undefined => {
    if (isServer) {
        return undefined;
    }

    const pathName = window.location.pathname;

    if (pathName.startsWith(PageUrl.PRODUCTS)) {
        return GTMPageType.PLP;
    }

    if (pathName.startsWith(PageUrl.PRODUCT)) {
        return GTMPageType.PDP;
    }

    if (pathName.startsWith(PageUrl.CHECKOUT_LOGIN)) {
        return GTMPageType.CHECKOUT_LOGIN;
    }

    if (pathName.startsWith(PageUrl.CHECKOUT_SHIPPING)) {
        return GTMPageType.CHECKOUT_SHIPPING;
    }

    if (pathName.startsWith(PageUrl.CHECKOUT_BILLING)) {
        return GTMPageType.CHECKOUT_BILLING;
    }

    if (pathName.startsWith(PageUrl.CHECKOUT_REVIEW)) {
        return GTMPageType.CHECKOUT_REVIEW;
    }

    if (pathName.startsWith(PageUrl.STORES)) {
        return GTMPageType.STORE_PAGE;
    }

    if (pathName.startsWith(PageUrl.ACCOUNT_REGISTRATION)) {
        return GTMPageType.ACCOUNT_REGISTRATION;
    }

    if (pathName.startsWith(PageUrl.ACCOUNT_FORGOT_PASSWORD)) {
        return GTMPageType.ACCOUNT_FORGOT_PASSWORD;
    }

    if (pathName.startsWith(PageUrl.ACCOUNT_RESET_PASSWORD)) {
        return GTMPageType.ACCOUNT_RESET_PASSWORD;
    }

    if (pathName.startsWith(PageUrl.PWA_CART)) {
        return GTMPageType.SHOPPING_BAG;
    }

    if (pathName.startsWith(PageUrl.COMPARE_PRODUCTS)) {
        return GTMPageType.PRODUCT_COMPARISON;
    }

    if (pathName.startsWith(PageUrl.SEARCH) || pathName.startsWith(PageUrl.PWA_SEARCH)) {
        return GTMPageType.SEARCH;
    }

    return GTMPageType.OTHER;
};

export const isNotEventTriggered = (event: GTMEventName): boolean => {
    if (isServer) {
        return true;
    }

    return !(window.dataLayer ?? []).map((e) => e.event).includes(event);
};
